import type { ZipCodeConnectedProps } from "dds/molecules/zip-code/zip-code-connected.client";

/**
 * The ZIP Code's visible copy as flat Storyblok fields on the carousel blok. Names
 * reuse production's `ciConfig` keys (zipcode-module.props.ts) where a field means
 * the same thing; every field is optional and an empty one falls back to the
 * component's default, which is production's copy.
 */
export interface ZipCopyFields {
  /** The trigger's lead-in. Production: `displayText`. */
  zip_display_text_label?: string;
  /** The trigger before a county is known. Production: `locationPillPlaceholder`. */
  zip_location_pill_placeholder_label?: string;
  /** The ZIP field's label. Production: `zipCodeLabel`. */
  zip_code_label?: string;
  /** The county field's label. Production's `countyLabel` was the placeholder text; it is split in two. */
  zip_county_label?: string;
  /** Shown in the county field while none is chosen. New; production's `countyLabel` value. */
  zip_county_placeholder_label?: string;
  /** The CTA once a ZIP is set. Production: `ctaLabel`. */
  zip_cta_label?: string;
  /** The CTA before a ZIP is set. Production: `addLocationCtaLabel`. */
  zip_add_location_cta_label?: string;
  /** Submit with a short ZIP. Production: `zipCodeInputErrorMsg`. */
  zip_code_input_error_content?: string;
  /** The ZIP found no county. Production: `zipCodeLookupErrorMsg`. */
  zip_code_lookup_error_content?: string;
  /** Submit with no county chosen. Production: `countySelectionErrorMsg`. */
  zip_county_selection_error_content?: string;
  /** The lookup is slow. Production: `countyApiHangingMsg`. */
  zip_county_api_hanging_content?: string;
  /** The lookup failed. Production: `countyApiErrorMsg`. */
  zip_county_api_error_content?: string;
  /** After a successful update. Production's fixed "Update Successful". */
  zip_success_content?: string;
  /** The info icon's text beside the trigger. Production: `displayTooltip.content`. */
  zip_tooltip_content?: string;
  /** The info icon's accessible name. Production: `displayTooltip.label`. */
  zip_tooltip_aria_label?: string;
  /** Border colour of the ZIP field and county dropdown. A CSS colour; one that is not is ignored. */
  zip_field_stroke_color?: string;
  /** The same border on hover. Set both. */
  zip_field_stroke_hover_color?: string;
}

/* Only the STRING-valued props may be a copy target. Plain `keyof` also admits
   `store`, `callbacks` and the booleans, and the cast at the foot of this file
   would then launder an author's string straight onto one of them. */
type StringProp<T> = { [K in keyof T]-?: T[K] extends string | undefined ? K : never }[keyof T];

const FIELD_TO_PROP: Record<keyof ZipCopyFields, StringProp<ZipCodeConnectedProps>> = {
  zip_display_text_label: "triggerLabel",
  zip_location_pill_placeholder_label: "locationPlaceholder",
  zip_code_label: "zipLabel",
  zip_county_label: "countyLabel",
  zip_county_placeholder_label: "countyPlaceholder",
  zip_cta_label: "ctaLabel",
  zip_add_location_cta_label: "addLocationCtaLabel",
  zip_code_input_error_content: "zipInputErrorMessage",
  zip_code_lookup_error_content: "zipLookupErrorMessage",
  zip_county_selection_error_content: "countySelectionErrorMessage",
  zip_county_api_hanging_content: "lookupHangingMessage",
  zip_county_api_error_content: "countyApiErrorMessage",
  zip_success_content: "successMessage",
  zip_tooltip_content: "tooltipContent",
  zip_tooltip_aria_label: "tooltipLabel",
  zip_field_stroke_color: "fieldStrokeColor",
  zip_field_stroke_hover_color: "fieldStrokeHoverColor"
};

/** Every copy field this module reads. Exported so the adapter's own copy of
 *  the list can be pinned equal to it — `lib/` cannot import `app/` at runtime,
 *  so the two are kept in step by a test rather than by the type system. */
export const ZIP_COPY_FIELD_NAMES = Object.keys(FIELD_TO_PROP) as (keyof ZipCopyFields)[];

const FIELDS = ZIP_COPY_FIELD_NAMES;

/**
 * Splits the ZIP copy fields off a blok's props. `copy` holds only the fields an
 * author filled in, as `ZipCodeConnected` props (an empty or non-string field is
 * dropped so the default shows); `rest` is everything else, so the fields never
 * reach an organism that spreads unknown props.
 */
export function splitZipCopy<T extends ZipCopyFields>(props: T): {
  copy: Partial<ZipCodeConnectedProps>;
  rest: Omit<T, keyof ZipCopyFields>;
} {
  const copy: Record<string, string> = {};
  const rest: Record<string, unknown> = { ...props };
  for (const field of FIELDS) {
    const value = (props as Record<string, unknown>)[field];
    delete rest[field];
    if (typeof value === "string" && value.trim() !== "") {
      copy[FIELD_TO_PROP[field]] = value;
    }
  }
  // One authored field names the trigger before a county is known, which is also what an empty ZIP shows.
  if (copy.locationPlaceholder) {
    copy.noZipLabel = copy.locationPlaceholder;
  }
  return {
    copy: copy as Partial<ZipCodeConnectedProps>,
    rest: rest as Omit<T, keyof ZipCopyFields>
  };
}
